import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Tag } from 'lucide-react';

const categories = ['Todos', 'Educação Financeira', 'Dicas e Truques', 'Investimentos', 'Dívidas', 'Planejamento', 'Novidades'];

const posts = [
  {
    slug: 'metodo-bola-de-neve-dividas',
    category: 'Dívidas',
    title: 'Método bola de neve: como quitar dívidas mais rápido (e com motivação)',
    excerpt: 'Entenda a estratégia que ajudou milhares de pessoas a sair das dívidas pagando as menores primeiro — e como o MyBank automatiza esse processo para você.',
    readTime: '7 min',
    date: '28 fev 2025',
    author: 'Diego Alves',
    authorRole: 'Head de IA',
    authorBg: 'bg-emerald-600',
    authorAvatar: 'DA',
    featured: true,
    image: '💸',
  },
  {
    slug: 'regra-50-30-20-funciona',
    category: 'Planejamento',
    title: 'A regra 50-30-20 ainda funciona em 2025? Testamos com dados reais',
    excerpt: 'A regra mais famosa do orçamento pessoal foi criada nos EUA dos anos 90. Analisamos se ela ainda faz sentido na realidade brasileira de hoje.',
    readTime: '9 min',
    date: '21 fev 2025',
    author: 'Juliana Costa',
    authorRole: 'Co-fundadora',
    authorBg: 'bg-purple-600',
    authorAvatar: 'JC',
    featured: true,
    image: '📊',
  },
  {
    slug: 'tesouro-direto-iniciantes',
    category: 'Investimentos',
    title: 'Tesouro Direto em 2025: guia completo para quem está começando',
    excerpt: 'Do cadastro à primeira compra, explicamos tudo sobre o investimento mais seguro do Brasil — com linguagem simples e exemplos práticos.',
    readTime: '12 min',
    date: '14 fev 2025',
    author: 'Diego Alves',
    authorRole: 'Head de IA',
    authorBg: 'bg-emerald-600',
    authorAvatar: 'DA',
    featured: false,
    image: '📈',
  },
  {
    slug: 'gastos-invisiveis',
    category: 'Educação Financeira',
    title: 'Os gastos invisíveis que estão consumindo seu dinheiro sem você perceber',
    excerpt: 'Assinaturas esquecidas, gorjetas automáticas, tarifas bancárias... Mapeamos os 8 vilões ocultos das finanças pessoais e como identificá-los.',
    readTime: '6 min',
    date: '10 fev 2025',
    author: 'Marina Lopes',
    authorRole: 'Head de Design',
    authorBg: 'bg-rose-600',
    authorAvatar: 'ML',
    featured: false,
    image: '🔍',
  },
  {
    slug: 'fundo-emergencia-quanto-guardar',
    category: 'Planejamento',
    title: 'Fundo de emergência: quanto você realmente precisa guardar?',
    excerpt: 'A resposta depende do seu perfil. Autônomos precisam mais. Servidores públicos, talvez menos. Calculamos o valor ideal para cada situação.',
    readTime: '8 min',
    date: '5 fev 2025',
    author: 'Rafael Mendes',
    authorRole: 'CEO',
    authorBg: 'bg-primary-600',
    authorAvatar: 'RM',
    featured: false,
    image: '🛡️',
  },
  {
    slug: 'ia-financas-pessoais',
    category: 'Novidades',
    title: 'Como a IA está transformando as finanças pessoais em 2025',
    excerpt: 'De previsão de gastos a alertas personalizados, a inteligência artificial deixou de ser ficção científica e virou ferramenta do dia a dia.',
    readTime: '10 min',
    date: '1 fev 2025',
    author: 'Diego Alves',
    authorRole: 'Head de IA',
    authorBg: 'bg-emerald-600',
    authorAvatar: 'DA',
    featured: false,
    image: '🤖',
  },
  {
    slug: 'cartao-credito-vilao-ou-aliado',
    category: 'Educação Financeira',
    title: 'Cartão de crédito: vilão ou aliado? Depende de como você usa',
    excerpt: 'Pontos, cashback, parcelamento sem juros... O cartão pode ser uma ferramenta poderosa — ou uma armadilha. A diferença está no comportamento.',
    readTime: '7 min',
    date: '26 jan 2025',
    author: 'Juliana Costa',
    authorRole: 'Co-fundadora',
    authorBg: 'bg-purple-600',
    authorAvatar: 'JC',
    featured: false,
    image: '💳',
  },
  {
    slug: 'metas-financeiras-smart',
    category: 'Dicas e Truques',
    title: 'Como definir metas financeiras que você vai realmente cumprir',
    excerpt: 'A maioria das metas falha não por falta de disciplina, mas por má definição. Aprenda o método SMART aplicado às finanças pessoais.',
    readTime: '5 min',
    date: '20 jan 2025',
    author: 'Rafael Mendes',
    authorRole: 'CEO',
    authorBg: 'bg-primary-600',
    authorAvatar: 'RM',
    featured: false,
    image: '🎯',
  },
];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('Todos');

  const filtered = activeCategory === 'Todos'
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  const featuredPosts = posts.filter((p) => p.featured);
  const regularPosts = filtered.filter((p) => !p.featured || activeCategory !== 'Todos');

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen">
      {/* Header */}
      <section className="py-20 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">Blog MyBank</h1>
          <p className="text-xl text-gray-400">
            Educação financeira sem jargão. Conteúdo prático para quem quer tomar
            decisões melhores com o próprio dinheiro.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured posts */}
        {activeCategory === 'Todos' && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Destaques</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-primary-400 dark:hover:border-primary-600 transition-all hover:shadow-lg bg-white dark:bg-gray-900"
                >
                  <div className="h-48 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-gray-800 flex items-center justify-center text-7xl">
                    {post.image}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
                        {post.category}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={11} /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-xl mb-2 group-hover:text-primary-600 transition-colors leading-tight">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full ${post.authorBg} flex items-center justify-center text-white text-xs font-bold`}>
                          {post.authorAvatar}
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{post.author} · {post.date}</span>
                      </div>
                      <span className="text-primary-600 group-hover:gap-2 flex items-center gap-1 text-sm font-medium transition-all">
                        Ler <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div className="flex items-center gap-2 flex-wrap mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* All posts grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(activeCategory === 'Todos' ? posts.filter((p) => !p.featured) : filtered).map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-primary-400 dark:hover:border-primary-600 transition-all hover:shadow-md bg-white dark:bg-gray-900"
            >
              <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center text-5xl">
                {post.image}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={11} className="text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">{post.category}</span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock size={11} /> {post.readTime}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                  {post.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full ${post.authorBg} flex items-center justify-center text-white text-xs font-bold`}>
                    {post.authorAvatar}
                  </div>
                  <span className="text-xs text-gray-500">{post.author} · {post.date}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-20 bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl p-10 text-center text-white">
          <h2 className="text-3xl font-bold mb-3">Receba conteúdo toda semana</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Assine nossa newsletter e receba artigos de educação financeira, dicas exclusivas e
            as novidades do MyBank diretamente no seu e-mail.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="seu@email.com"
              className="flex-1 px-5 py-3 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Assinar
            </button>
          </form>
          <p className="text-white/50 text-xs mt-3">Sem spam. Cancele quando quiser.</p>
        </div>
      </div>
    </div>
  );
};

export default Blog;
